import React, {useContext, useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useAuthInfo, useRedirectFunctions} from "@propelauth/react";

import {BulkInvitePublicView, claimBulkInvite, getBulkInvitePublic} from "../../api/bulk_invite_api";
import {UserContext} from "../../contexts/UserContext";
import {CompanyType} from "../../models/company";
import {AnonymousHeader} from "../../layouts/Header";
import {Button} from "../ui/button";
import {Card, CardContent, CardHeader, CardTitle} from "../ui/card";
import {Spinner} from "../ui/spinner";
import {SelectScopes} from "./SelectScopes";

/**
 * Landing page for a bulk invite link: /invite/:token
 *
 * The recipients of this link are unknown to us - the developer sent it out
 * themselves - so there is no email allowlist and no magic link. Everyone
 * signs up normally and is assigned as a builder.
 *
 * This route is registered in every branch of the router (logged out in
 * App.tsx, and each company_type branch in App.router.tsx) so the user comes
 * back to this same url at every step:
 *
 *   1. not logged in        -> sign up (propelauth returns here)
 *   2. company_type not_set -> /company-setup?as=builder&next=/invite/:token
 *   3. developer / broker   -> notice, this link is for bidders
 *   4. builder              -> claim, then pick scopes
 */
export function BulkInvitePage() {
    const {token} = useParams();
    const navigate = useNavigate();
    const {isLoggedIn, accessToken} = useAuthInfo();
    const {redirectToSignupPage, redirectToLoginPage} = useRedirectFunctions();
    const userContext = useContext(UserContext);

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [invite, setInvite] = useState<BulkInvitePublicView | null>(null);
    const [claimed, setClaimed] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    const companyType = userContext?.company_type;

    // 1. is the link any good, and what project is it for
    useEffect(() => {
        getBulkInvitePublic(token!)
            .then((data) => {
                setInvite(data);
                setIsLoading(false);
            })
            .catch(() => {
                setInvite({valid: false, reason: 'not_found'});
                setIsLoading(false);
            });
    }, [token]);

    // 2. signed up but no company yet -> company setup, forced to builder,
    //    and come straight back here afterwards.
    useEffect(() => {
        if (invite?.valid && isLoggedIn && companyType === CompanyType.not_set) {
            navigate(`/company-setup?as=builder&next=/invite/${token}`);
        }
    }, [invite, isLoggedIn, companyType, token, navigate]);

    // 3. builder with a company -> claim the invite (idempotent)
    useEffect(() => {
        if (!invite?.valid || !isLoggedIn || !accessToken) return;
        if (companyType !== CompanyType.builder) return;
        if (claimed) return;

        claimBulkInvite(token!, accessToken)
            .then(() => setClaimed(true))
            .catch((e) => setError(e.message || 'Could not join this project.'));
    }, [invite, isLoggedIn, accessToken, companyType, claimed, token]);

    if (isLoading) return <Spinner/>;

    // logged out we render standalone, logged in we are already inside AppLayout
    const wrap = (children: React.ReactNode) => (
        <div className={isLoggedIn ? "flex flex-col" : "min-h-screen flex flex-col"}>
            {!isLoggedIn && <AnonymousHeader/>}
            <div className="flex-1 flex items-start justify-center pt-10">
                <div className="max-w-2xl w-full p-6">{children}</div>
            </div>
        </div>
    );

    if (!invite?.valid) {
        const message = invite?.reason === 'revoked'
            ? 'This invite link has been turned off by the project team.'
            : invite?.reason === 'expired'
                ? 'This invite link has expired.'
                : 'This invite link is not valid.';
        return wrap(
            <Card>
                <CardHeader><CardTitle className="font-normal !text-[17px]">Invite unavailable</CardTitle></CardHeader>
                <CardContent><p className="text-sm text-gray-600">{message}</p></CardContent>
            </Card>
        );
    }

    const projectLine = invite.project_number
        ? `${invite.project_number} - ${invite.project_name}`
        : invite.project_name;

    // not logged in -> sign up. propelauth sends them back to this exact url.
    if (!isLoggedIn) {
        return wrap(
            <Card>
                <CardHeader>
                    <CardTitle className="font-normal !text-[17px]">
                        You have been invited to bid on {projectLine}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <p className="text-sm text-gray-600">
                        {invite.developer_company} is accepting bids on this project.
                        Create an account to see the scopes and submit pricing.
                    </p>
                    <div className="flex gap-2">
                        <Button
                            onClick={() => redirectToSignupPage({postSignupRedirectUrl: window.location.href})}>
                            Sign up to bid
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => redirectToLoginPage({postLoginRedirectUrl: window.location.href})}>
                            I already have an account
                        </Button>
                    </div>
                </CardContent>
            </Card>
        );
    }

    // developers and brokers cannot bid on a project
    if (companyType === CompanyType.developer || companyType === CompanyType.broker) {
        return wrap(
            <Card>
                <CardHeader><CardTitle className="font-normal !text-[17px]">This link is for bidders</CardTitle></CardHeader>
                <CardContent>
                    <p className="text-sm text-gray-600">
                        This invite link is meant for builders bidding on {projectLine}.
                        Your account is registered as a {companyType}.
                    </p>
                </CardContent>
            </Card>
        );
    }

    // mid-redirect to company setup
    if (companyType === CompanyType.not_set) return <Spinner/>;

    if (error) {
        return wrap(
            <Card>
                <CardHeader><CardTitle className="font-normal !text-[17px]">Could not join</CardTitle></CardHeader>
                <CardContent><p className="text-sm text-red-600">{error}</p></CardContent>
            </Card>
        );
    }

    if (!claimed) return <Spinner/>;

    return wrap(
        <SelectScopes
            token={token!}
            projectLine={projectLine || ''}
            developerCompany={invite.developer_company || ''}
        />
    );
}

export default BulkInvitePage;
